import { Component, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { SubTask, Task, TaskTypeAction } from '@k12/models';

interface LeaReleaseFormData {
  studentName?: string | null;
  programTypeCode?: string | null;
  description?: string | null;
  householdId?: string | null;
  programPartnerId?: string | null;
  studentId?: string | null;
  academicYearId?: number | null;
  programTypeId?: number | null;
  isLeaReleaseApproved?: boolean | null;
  isLeaReleaseSigned?: boolean | null;
  parentFirstName?: string | null;
  parentLastName?: string | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-lea-release-form',
  standalone: true,
  imports: [ReactiveFormsModule, MatButtonModule, MatCardModule, MatInputModule, MatRadioModule],
  templateUrl: './lea-release-form.component.html',
  styleUrl: './lea-release-form.component.scss'
})
export class LeaReleaseFormComponent implements OnInit {
  @Input() task!: Task | null;
  @Input() taskAction!: SubTask | null;
  @Input() taskTypeAction!: TaskTypeAction | null;
  @Input() isReadOnly = false;
  @Output() submitFormData = new EventEmitter<string>();

  private readonly fb = inject(FormBuilder);

  protected form!: FormGroup;
  protected context: LeaReleaseFormData = {};

  public ngOnInit(): void {
    const raw = this.taskAction?.formData ? this.safeParseJson(this.taskAction.formData) : {};

    this.context = this.normalizeKeys(raw) as LeaReleaseFormData;

    this.form = this.fb.group({
      studentName: [{ value: this.context.studentName ?? '', disabled: true }],
      programTypeCode: [{ value: this.context.programTypeCode ?? '', disabled: true }],
      description: [{ value: this.context.description ?? '', disabled: true }],
      isLeaReleaseSigned: [this.context.isLeaReleaseSigned ?? null, Validators.requiredTrue],
      parentFirstName: [this.context.parentFirstName ?? '', Validators.required],
      parentLastName: [this.context.parentLastName ?? '', Validators.required]
    });

    if (this.isReadOnly) {
      this.form.disable({ emitEvent: false });
    }
  }

  protected get programLabel(): string {
    // LEA release forms are ESA+ specific; ProgramTypeId 1 maps to ESA+.
    return this.context.programTypeId === 1 ? 'ESA+' : (this.context.programTypeCode ?? 'ESA+');
  }

  protected get releaseDecisionLabel(): string {
    const approved = this.context.isLeaReleaseApproved ?? this.context.isLeaReleaseSigned;
    if (approved === true) return 'Confirmed — released to the LEA';
    if (approved === false) return 'Not confirmed';
    return 'Pending household response';
  }

  private safeParseJson(raw: string): Record<string, unknown> {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }

  private normalizeKeys(raw: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(Object.entries(raw).map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), v]));
  }

  protected isFormReady(): boolean {
    return this.form.valid && this.form.dirty;
  }

  protected onBack(): void {
    globalThis.history.back();
  }

  protected submitForm(): void {
    if (!this.isFormReady()) return;

    this.submitFormData.emit(
      JSON.stringify({
        ...this.context,
        isLeaReleaseSigned: this.form.getRawValue().isLeaReleaseSigned as boolean,
        isLeaReleaseApproved: this.form.getRawValue().isLeaReleaseSigned as boolean,
        parentFirstName: this.form.getRawValue().parentFirstName as string,
        parentLastName: this.form.getRawValue().parentLastName as string
      })
    );
  }
}
